import { Injectable, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import * as moment from 'moment';
import { L10N_CONFIG, L10nConfig, L10N_LOCALE, L10nLocale, L10nIntlService, L10nTranslationService } from 'angular-l10n';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  private SERVER_DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
  private SERVER_DATE_FORMAT = "YYYY-MM-DD";
  private DATETIME_FORMAT = "DD-MM-YYYY HH:mm";
  private DATE_FORMAT = "DD-MM-YYYY";

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    @Inject(L10N_CONFIG) private l10nConfig: L10nConfig,
    private intl: L10nIntlService,
    private translation: L10nTranslationService,
  ) { }

  getNgbDatepickerDateFromDate(date: Date): NgbDateStruct {
    return {
      day: date.getUTCDate(),
      month: date.getUTCMonth() + 1,
      year: date.getUTCFullYear()
    };
  }

  // 2020-04-15
  getNgbDatepickerDateFromDateString(date: string) {
    if (date == null || date == undefined || date == '') {
      return '';
    }

    let utc = moment.utc(date);

    return {
      day: utc.date(),
      month: utc.month() + 1,
      year: utc.year()
    };
  }

  getNgbDatepickerDateNow(): NgbDateStruct {
    let now = new Date();

    return this.getNgbDatepickerDateFromDate(now);
  }

  getNgbTimepickerDefault(): NgbTimeStruct {
    return {
      hour: 13,
      minute: 0,
      second: 0
    };
  }

  // 12:45
  getNgbTimepickerFromTimeString(time: string) {
    if (time == null || time == undefined || time == '' || time == ':') {
      return '';
    }

    let timeSplitted = time.split(":");

    return {
      hour: parseInt(timeSplitted[0]),
      minute: parseInt(timeSplitted[1]),
      second: 0
    };
  }

  getLocalDateFromNgbDateStruct(ngbDate?: NgbDateStruct | string) {
    if (ngbDate === undefined || ngbDate === null || ngbDate === '') {
      return null;
    }
    ngbDate = <NgbDateStruct>ngbDate;

    let year = ngbDate.year;

    let month = ngbDate.month + "";
    if (month.length === 1) {
      month = "0" + month;
    }

    let day = ngbDate.day + "";
    if (day.length === 1) {
      day = "0" + day;
    }

    let localDate = year + "-" + month + "-" + day;

    return localDate;
  }

  getLocalDateTimeFromNgbDatepickerDateTime(ngbDate: NgbDateStruct | string, ngbTime: NgbTimeStruct | string) {
    if (ngbDate === undefined || ngbDate === null || ngbDate === '') {
      return null;
    }
    ngbDate = <NgbDateStruct>ngbDate;

    if (ngbTime === undefined || ngbTime === null || ngbTime === '') {
      return null;
    }
    ngbTime = <NgbTimeStruct>ngbTime;

    let year = ngbDate.year;

    let month = ngbDate.month + "";
    if (month.length === 1) {
      month = "0" + month;
    }

    let day = ngbDate.day + "";
    if (day.length === 1) {
      day = "0" + day;
    }

    let hour = ngbTime.hour + "";
    if (hour.length === 1) {
      hour = "0" + hour;
    }

    let minute = ngbTime.minute + "";
    if (minute.length === 1) {
      minute = "0" + minute;
    }

    let second = ngbTime.second + "";
    if (second.length === 1) {
      second = "0" + second;
    }

    let localDateTime = year + "-" + month + "-" + day + " " + hour + ":" + minute + ":" + second;

    return localDateTime;
  }

  getAgeFromNgbDateStruct(ngbDate?: NgbDateStruct | string) {
    let localDate = this.getLocalDateFromNgbDateStruct(ngbDate);
    if (localDate === null) {
      return null;
    }

    return this.getAgeFromDateString(localDate);
  }

  getAgeFromDateString(dateString: string) {
    if (dateString === undefined || dateString === null || dateString === '') {
      return null;
    }

    let birthday = new Date(dateString);

    return this.getAgeFromDate(birthday);
  }

  getAgeFromDate(birthday: Date) {
    var ageDifMs = Date.now() - birthday.getTime();
    var ageDate = new Date(ageDifMs);

    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  getServerDateFromNgbDatepickerDate(ngbDate: NgbDateStruct | string) {
    return this.getServerDateFromLocalDate(this.getLocalDateFromNgbDateStruct(ngbDate));
  }

  getServerDateFromLocalDate(localDate: string) {
    if (localDate == null || localDate == undefined || localDate == '') {
      return '';
    }

    let momentDate = moment(localDate);

    return momentDate.format(this.SERVER_DATE_FORMAT);
  }

  getServerDatetimeFromNgbDatepickerDateTime(ngbDate: NgbDateStruct | string, ngbTime: NgbTimeStruct | string) {
    return this.getServerDatetimeFromLocalDatetime(this.getLocalDateTimeFromNgbDatepickerDateTime(ngbDate, ngbTime));
  }

  getServerDatetimeFromLocalDatetime(localDatetime: string) {
    if (localDatetime == null || localDatetime == undefined || localDatetime == '') {
      return '';
    }

    let momentDate = moment(localDatetime);

    return momentDate.utc().format(this.SERVER_DATETIME_FORMAT);
  }

  getServerTimeFromNgbTime(ngbTime: NgbTimeStruct | string) {
    if (ngbTime === undefined || ngbTime === null || ngbTime === '') {
      return null;
    }
    ngbTime = <NgbTimeStruct>ngbTime;

    let hour = ngbTime.hour + "";
    if (hour.length === 1) {
      hour = "0" + hour;
    }

    let minute = ngbTime.minute + "";
    if (minute.length === 1) {
      minute = "0" + minute;
    }

    return hour + ":" + minute;
  }

  getCurrentLocale(): L10nLocale {
    return this.locale;
  }

  getCurrentLanguage(): string {
    return this.locale.language;
  }

  getDefaultLanguage(): string {
    return this.l10nConfig.defaultLocale.language;
  }

  getLocaleText(locale: L10nLocale): string {
    let schema = this.l10nConfig.schema;
    let localeText = null;

    schema.forEach(element => {
      if (element.locale.language === locale.language) {
        localeText = element.text;
      }
    });

    return localeText;
  }

  // 2020-04-15
  getFormattedDate(date: string) {
    if (date == null || date == undefined || date == '') {
      return '';
    }

    let utc = moment.utc(date);

    return this.intl.formatDate(utc.format(), { dateStyle: 'full' });
  }

  // 2020-04-15 18:00:00
  getFormattedDatetime(datetime: string) {
    if (datetime == null || datetime == undefined || datetime == '') {
      return '';
    }

    let utc = moment.utc(datetime);

    return this.intl.formatDate(utc.format(), { dateStyle: 'full', timeStyle: 'short' });
  }

  getFormattedCurrency(value: string) {
    if (value == null || value == undefined || value == '') {
      return '';
    }

    return this.intl.formatNumber(value, { digits: '1.2-2', style: 'currency' });
  }

  getParameterFromFieldId(parameters: Array<any>, fieldId: string) {
    for (let parameter of parameters) {
      if (parameter.fieldId === fieldId) {
        return parameter;
      }
    }

    return null;
  }

  getParameterValueFromFieldId(parameters: Array<any>, fieldId: string, form: FormGroup) {
    let foundParameter = null;

    for (let parameter of parameters) {
      if (parameter.fieldId === fieldId) {
        foundParameter = parameter;

        break;
      }
    }

    if (foundParameter !== null) {
      return this.getParameterValue(foundParameter, form);
    }

    return null;
  }

  getParameterValue(parameter: any, form: FormGroup) {
    let parameterValue = null;

    switch (parameter.type) {
      case 'text':
        parameterValue = form.get(parameter.id).value;
        break;

      case 'date':
        parameterValue = this.getServerDateFromNgbDatepickerDate(form.get(parameter.id).value);
        break;

      case 'time':
        parameterValue = this.getServerTimeFromNgbTime(form.get(parameter.id).value);
        break;

      case 'enum':
        parameterValue = form.get(parameter.id).value;
        break;

      case 'multienum': {
        let value = form.get(parameter.id).value;
        if (value !== undefined && value !== null && value !== "") {
          value = "^" + value.join("^,^") + "^";
        }
        parameterValue = value;
      }
        break;

      case 'photo': {
        let value = form.get(parameter.id).value;
        if (value !== undefined && value !== null && value !== "") {
          let n = value.lastIndexOf("\\");
          if (n === -1) {
            n = value.lastIndexOf("/");
          }
          value = value.substring(n + 1);
        }
        parameterValue = value;
      }
        break;

      default:
        break;
    }

    return parameterValue;
  }

  getParameterTranslation(parameter: any, form: FormGroup, language: string = this.getDefaultLanguage()) {
    let parameterLabel = null;

    switch (parameter.type) {
      case 'text':
        parameterLabel = form.get(parameter.id).value;
        break;

      case 'date':
        parameterLabel = this.getFormattedDateFromNgbDatepickerDate(form.get(parameter.id).value);
        break;

      case 'time':
        parameterLabel = this.getServerTimeFromNgbTime(form.get(parameter.id).value);
        break;

      case 'enum': {
        let parameterValue = form.get(parameter.id).value;
        if (parameterValue !== undefined && parameterValue !== null && parameterValue !== '') {
          parameterLabel = this.translation.translate(this.getEnumLabel(parameter.options, parameterValue), null, language);
        } else {
          parameterLabel = null;
        }
      }
        break;

      case 'multienum': {
        let parameterValues = form.get(parameter.id).value;
        if (parameterValues !== undefined && parameterValues !== null && parameterValues !== '') {
          parameterLabel = '';
          let firstValue = true;
          for (let parameterValue of parameterValues) {
            if (firstValue === true) {
              firstValue = false;
            } else {
              parameterLabel += ', ';
            }

            parameterLabel += this.translation.translate(this.getEnumLabel(parameter.options, parameterValue), null, language);
          }
        } else {
          parameterLabel = null;
        }
      }
        break;

      default:
        break;
    }

    return parameterLabel;
  }

  // 15-04-2020
  getFormattedDateFromNgbDatepickerDate(ngbDate: NgbDateStruct | string) {
    let localDate = this.getLocalDateFromNgbDateStruct(ngbDate);
    if (localDate === null) {
      return null;
    }

    return this.getFormattedDateFromLocalDate(localDate);
  }

  getFormattedDateFromLocalDate(localDate: string) {
    if (localDate == null || localDate == undefined || localDate == '') {
      return '';
    }

    let momentDate = moment(localDate);

    return momentDate.format(this.DATE_FORMAT);
  }

  getEnumLabel(enumOptions: any, value) {
    let enumLabel = null;

    for (let enumOption of enumOptions) {
      if (enumOption.value === value) {
        enumLabel = enumOption.label;

        break;
      }
    }

    return enumLabel;
  }

  getEnumValue(enumOptions: any, label) {
    if (label === null || label === "") {
      return null;
    }
    label = label + "";
    let lcLabel = label.toLowerCase();

    let schema = this.l10nConfig.schema;    

    for (let enumOption of enumOptions) {
      for (let element of schema) {        
        let translatedLabel = this.translation.translate(enumOption.label, null, element.locale.language);
        if (translatedLabel === null || translatedLabel === "") {
          continue;
        }
        translatedLabel = translatedLabel + "";
        let lcTranslatedLabel = translatedLabel.toLowerCase();

        if (lcLabel == lcTranslatedLabel) {
          return enumOption.value;
        }
      }      
    }
    
    return null;
  }

  cloneObject(obj) {
    var copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || "object" != typeof obj) return obj;

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (var i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.cloneObject(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (var attr in obj) {
        if (obj.hasOwnProperty(attr)) copy[attr] = this.cloneObject(obj[attr]);
      }
      return copy;
    }

    return null;
  }
}
